import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useState } from "react";
import PositiveIcon from "../../assets/meeting/positive-icon.svg";
import TopicSentimentIcon from "../../assets/meeting/topic-sentiment-icon.svg";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { Box } from "@mui/system";
import { highlightKeyword } from "../../utils/highlightKeyword";
import "../../styles/common.css";
import MagicStickicon from "../../assets/common/ask7g-selected.svg";
import FeedBackIcon from "../../assets/common/feedBackIcon.svg";
import FeedbackPopup from "../Common/FeedbackPopUp";
import useGlobalContext from "../../context/useGlobalContext";
import UserInteractionsService from "../../services/interactions/Interactions";
import ProgressSpinner from "../Common/ProgressSpinner";

interface TopicPageprops {
  interactionId: string;
  keyword?: string;
  groupId?: string | null;
}

const TopicsPage = ({ interactionId, keyword = "", groupId  }: TopicPageprops) => {
  const [summaryByTopic, setSummaryByTopic] = useState<any[]>([]);
  const { showSnackbar } = useDisplaySnackbar();
  const [expandedIndex, setExpandedIndex] = useState<string | number | false>(false);
  const { featureMap, impersonationDetails } = useGlobalContext();
  const userFeedback = featureMap?.idp_user_feedback === true;
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [expandedDomain, setExpandedDomain] = useState<number | false>(0);
  const impersonatingUserPorId = impersonationDetails?.impersonatingUserPorId;
  const accessLevel = impersonationDetails?.accessType;

  const {
    data: insightsData,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    error,
  } = UserInteractionsService.useGetConversationInsights(
    {
      interactionId: interactionId,
      groupId: groupId || "",
      impersonatingPorId: impersonatingUserPorId,
      accessLevel: accessLevel,
    },
    !!interactionId
  );

  useEffect(() => {
    setShowSpinner(isLoading || isFetching);
    if (isSuccess) {
      const topicSummaries =
        insightsData?.data?.[0]?.summaryByTopic?.summary_by_topic ?? [];
      setSummaryByTopic(topicSummaries);
    }
    if (isError) {
      const errorMessage =
        error?.response?.data?.error || "Failed to load Topics.";
      showSnackbar("error", errorMessage);
    }
  }, [isSuccess, isLoading, insightsData, isError]);

  useEffect(() => {
    if (keyword && Array.isArray(summaryByTopic)) {
      const lowerKeyword = keyword.toLowerCase();
      const index = summaryByTopic.findIndex((item: any) =>
        item.topic?.toLowerCase()?.includes(lowerKeyword)
      );
      if (index !== -1) {
        setExpandedIndex(index);
      }
    }
  }, [summaryByTopic, keyword]);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [selectedTopicIndex, setSelectedTopicIndex] = useState<number | null>(null);
  const [openTooltipIndex, setOpenTooltipIndex] = useState<string | null>(null);
  const [badgeData, setBadgeData] = useState<any>(null);
  const [feedbackReference, setFeedbackReference] = useState<string>("");
  const [openRationaleIndex, setOpenRationaleIndex] = useState<string | null>(null);

  const handleFeedbackClick = (index: number, badge: any, topic: string) => {
    setSelectedTopicIndex(index);
    setBadgeData(badge);
    setFeedbackReference(topic);
    setOpenRationaleIndex(null);
    setOpenTooltipIndex(null); 
    setFeedbackOpen(true);
  };

  const handleFeedbackSubmit = () => {
    console.log("Topic index:", selectedTopicIndex);
    showSnackbar("success", "Feedback submitted successfully.");
  };

  const RationaleTooltip = ({ points, onFeedbackClick, tooltipKey }: { points: string[]; onFeedbackClick?: () => void; tooltipKey: string }) => (
    <Box
      sx={{
        backgroundColor: "#fff",
        color: "#000",
        p: 1.5,
        borderRadius: "8px",
        boxShadow: "0px 4px 16px rgba(0,0,0,0.15)",
        maxWidth: 320,
        maxHeight: 700,
        overflowY: "auto",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 0.8 }}>
      <img src={MagicStickicon} alt="" style={{height: "14px", width: "17px"}} />
        <Typography
          sx={{ fontWeight: 600, fontSize: "15px", color: "#B902CA",  }}
        >
           Rationale
        </Typography>
      {userFeedback &&
      <Tooltip 
        title="Give feedback on this tag" 
        placement="top" 
        arrow
        classes={{ tooltip: "custom-tooltip", arrow: "custom-tooltip-arrow" }}
        open={openTooltipIndex === tooltipKey && !feedbackOpen}
        onOpen={() => setOpenTooltipIndex(tooltipKey)}
        onClose={() => setOpenTooltipIndex(null)}
        >
        <img
          src={FeedBackIcon}
          alt="feedback"
          className="feedback-icon"
          onClick={onFeedbackClick}
        />
      </Tooltip>
      }
      </Box>
  
      {/* Bullet points */}
      <ul style={{ margin: 0, paddingLeft: 18,  listStyleType: "disc", }}>
        {points.map((p, i) => (
          <li
            key={i}
            style={{
              listStyleType: "disc",
              display: "list-item",
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: 1.4,
              marginBottom: 4,
            }}
          >
            {p}
          </li>
        ))}
      </ul>
    </Box>
  );

  const domainGroups: Record<string, any[]> = {};
  const standaloneTopics: any[] = [];

  summaryByTopic.forEach((item: any) => {
    if (!item.domainTopic) {
      standaloneTopics.push(item);
    } else {
      if (!domainGroups[item.domainTopic]) {
        domainGroups[item.domainTopic] = [];
      }
      domainGroups[item.domainTopic].push(item);
    }
  });

  const hasGrouping = Object.keys(domainGroups).length > 0;

  useEffect(() => {
    if (hasGrouping) {
      setExpandedDomain(0);
    }
  }, [hasGrouping]);

  const getTopicTags = (item: any) => {
    const topicClassification = item?.topicClassification;
    const topicSentiment = item?.topicSentiment;

    let scoreValue: number | null = null;
    if (topicSentiment !== null && topicSentiment !== "") {
      const parsedScore = Number(topicSentiment);
      if (!isNaN(parsedScore)) scoreValue = parsedScore;
    }

    const isPositive =
      (scoreValue !== null && scoreValue >= 8) ||
      topicSentiment?.toLowerCase() === "positive";

    const isNeutral = topicSentiment?.toLowerCase() === "neutral";
    const isClassificationNeutral =
      topicClassification?.toLowerCase() === "neutral";

    const isNegative =
      (scoreValue !== null && scoreValue <= 4) ||
      topicSentiment?.toLowerCase() === "negative";

    let classificationColor: string | null = null;

    if (topicClassification?.toLowerCase() === "client_controllable") {
      classificationColor = "#007BFF";
    } else if (topicClassification?.toLowerCase() === "account_controllable") {
      classificationColor = "#BD00CD";
    }

    return {
      topicClassification,
      classificationColor,
      isPositive,
      isNegative,
      isNeutral,
      isClassificationNeutral,
    };
  };

  const getTopicBadgeColor = ({
    classificationColor,
    isPositive,
  }: {
    classificationColor: string | null;
    isPositive: boolean;
  }) => {
    if (isPositive) return "#4CAF50";

    return classificationColor || "#000";
  };

  const getTopicBadgeIcon = ({
    topicClassification,
    isPositive,
    isNegative,
    isNeutral,
  }: {
    topicClassification?: string;
    isPositive: boolean;
    isNegative: boolean;
    isNeutral: boolean;
  }) => {
    if (isPositive) {
      return {
        src: PositiveIcon,
        alt: "Positive",
        filter:
          "invert(48%) sepia(84%) saturate(577%) hue-rotate(70deg) brightness(95%) contrast(88%)",
      };
    }

    if (isNegative || isNeutral) {
      return {
        src: TopicSentimentIcon,
        alt: "classification",
        filter:
          topicClassification?.replace(/_/g, " ") === "client controllable"
            ? "invert(34%) sepia(99%) saturate(1914%) hue-rotate(199deg) brightness(95%) contrast(90%)"
            : "invert(14%) sepia(91%) saturate(7461%) hue-rotate(293deg) brightness(97%) contrast(108%)",
      };
    }

    return null;
  };


  const renderTopicAccordion = (item: any, index: number, key: string) => {
    const tooltipKey = key;
    const alphabet = String.fromCharCode(97 + index);

    const topicTitle = `${alphabet}. ${
      item.topic?.replace(/^Topic\s*\d+\.\s*/, "") || "Untitled"
    }`;

    const summary = item?.summary;
    const topicSentiment = item?.topicSentiment;
    const rationalePoints =
      item?.topicClassificationRationale?.split("\n").filter(Boolean) || [];

    const isExpanded = expandedIndex === key;

    const {
      topicClassification,
      classificationColor,
      isPositive,
      isNegative,
      isNeutral,
      isClassificationNeutral,
    } = getTopicTags(item);

    return (
      <Accordion
        key={key}
        expanded={isExpanded}
        onChange={() => setExpandedIndex(isExpanded ? false : key)}
        disableGutters
        className="topics__accordion"
        sx={{ position: "relative", overflow: "visible"}}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            "& .MuiAccordionSummary-expandIconWrapper": {
              color: "#9A9AA8",
            },
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              color: "#6036D6",
            },
          }}
          aria-controls={`panel-${index}-content`}
          id={`panel-${index}-header`}
          className="topics__accordion-summary"
        >
          <Box className="topics__accordion-title-wrapper">
            <Typography
              component="span"
              className={`topics__accordion-title ${isExpanded ? "expand" : ""}`}
            >
              {highlightKeyword(topicTitle, keyword || "")}
            </Typography>

            {!isClassificationNeutral && (classificationColor || isNegative || isNeutral) && (
              <Tooltip
              placement="right-start"
              open={openRationaleIndex === tooltipKey && !feedbackOpen}
              onOpen={() => {
                setOpenTooltipIndex(null);
                setOpenRationaleIndex(tooltipKey);
              }}
              onClose={() => setOpenRationaleIndex(null)}
              disableFocusListener
              disableTouchListener
              title={<RationaleTooltip points={rationalePoints} 
              onFeedbackClick={() =>
                handleFeedbackClick(index, {
                  topicSentiment,
                  topicClassification,
                  isPositive,
                  isNegative,
                  isNeutral,
                  classificationColor,
                }, item.topic)
              }
              tooltipKey={tooltipKey} />}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "transparent",
                    p: 0,
                  },
                },
                arrow: {
                  sx: {
                    color: "#fff",
                  },
                },
              }}
            >
              <Box
                className="topics__badge"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                {
                  (isNegative || isNeutral) && (
                    <Box
                    component="img"
                    src={TopicSentimentIcon}
                    alt="classification"
                    className="topics__classification-icon"
                    sx={{
                      width: 16,
                      height: 16,
                      filter:
                      topicClassification?.replace(/_/g, " ") ===
                      "client controllable"
                      ? "invert(34%) sepia(99%) saturate(1914%) hue-rotate(199deg) brightness(95%) contrast(90%)"
                      : "invert(14%) sepia(91%) saturate(7461%) hue-rotate(293deg) brightness(97%) contrast(108%)",
                    }}
                    />
                  )}
                {
                  topicClassification &&
                <Typography
                  className="topics__classification-text"
                  sx={{
                    color: classificationColor,
                    fontSize: "0.75rem",
                  }}
                >
                  {topicClassification.replace(/_/g, " ")}
                </Typography>
                }
              </Box>
              </Tooltip>
            )}

            {isPositive && (
              <Tooltip
              placement="right-start"
              open={openRationaleIndex === tooltipKey && !feedbackOpen}
              onOpen={() => {
                setOpenTooltipIndex(null);
                setOpenRationaleIndex(tooltipKey);
              }}
              onClose={() => setOpenRationaleIndex(null)}
              disableFocusListener
              disableTouchListener
              title={<RationaleTooltip points={rationalePoints} 
                onFeedbackClick={() =>
                  handleFeedbackClick(index, {
                    topicSentiment,
                    topicClassification,
                    isPositive,
                    isNegative,
                    isNeutral,
                    classificationColor,
                  },item.topic)
                } 
                tooltipKey={tooltipKey} />}
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "transparent",
                    p: 0,
                  },
                },
                arrow: {
                  sx: {
                    color: "#fff",
                  },
                },
              }}
            >
              <Box
                className="topics__badge"
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <img
                  src={PositiveIcon}
                  alt="Positive"
                  className="topics__positive-icon"
                  style={{
                    width: 16,
                    height: 16,
                    filter:
                      "invert(48%) sepia(84%) saturate(577%) hue-rotate(70deg) brightness(95%) contrast(88%)",
                  }}
                />
                <Typography
                  className="topics__positive-text"
                  sx={{ fontSize: "0.75rem" }}
                >
                  Positive
                </Typography>
              </Box>
              </Tooltip>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails className="topics__details">
          <Box className="topics__summary-list-wrapper">
            {Array.isArray(summary) && summary.length > 0 ? (
              <ul className="topics__summary-list">
                {summary.map((point: string, i: number) => (
                  <li key={i} className="topics__summary-item">
                    {point}
                  </li>
                ))}
              </ul>
            ) : summary ? (
              <ul className="topics__summary-list">
                <li className="topics__summary-item">{summary}</li>
              </ul>
            ) : (
              <Typography className="topics__no-summary">
                No summary available.
              </Typography>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
  )}
   
  return (
    <>
      <div className="topics__wrapper">
        {summaryByTopic.length === 0 && !showSpinner && (
          <Box className="topics__empty-container">
            <Typography className="topics__empty-message">
              No topics to view.
            </Typography>
          </Box>
        )}

        <Box className="topics__accordion-container">
          {hasGrouping ? (
            <AccordionDetails>
              {Object.entries(domainGroups).map(([domain, topics], dIndex) => {
                const isDomainExpanded = expandedDomain === dIndex;
                const topicCount = topics.length;
                const classificationMap: Record<
                  string,
                  {
                    count: number;
                    labelColor: string;
                    countColor: string;
                    icon: ReturnType<typeof getTopicBadgeIcon>;
                  }
                > = {};
                topics.forEach((t: any) => {
                  const {
                    topicClassification,
                    classificationColor,
                    isPositive,
                    isNegative,
                    isNeutral,
                    isClassificationNeutral,
                  } = getTopicTags(t);

                  if (!topicClassification || isClassificationNeutral) return;

                  const key = topicClassification.toLowerCase();
                  const countColor = getTopicBadgeColor({
                    classificationColor,
                    isPositive,
                  });
                  const icon = getTopicBadgeIcon({
                    topicClassification,
                    isPositive,
                    isNegative,
                    isNeutral,
                  });

                  if (!classificationMap[key]) {
                    classificationMap[key] = {
                      count: 0,
                      labelColor: classificationColor || "#000",
                      countColor,
                      icon,
                    };
                  }

                  classificationMap[key].count++;

                  if (isPositive) {
                    classificationMap[key].labelColor = countColor;
                    classificationMap[key].countColor = countColor;
                    classificationMap[key].icon = icon;
                  } else if (icon && !classificationMap[key].icon) {
                    classificationMap[key].icon = icon;
                  }
                });
                const formatLabel = (key: string) => key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
                return (
                  <Accordion 
                    key={dIndex} 
                    expanded={isDomainExpanded}
                    onChange={() =>
                      setExpandedDomain(isDomainExpanded ? false : dIndex)
                    }
                    disableGutters
                    className="topics__accordion__group"
                    sx={{ position: "relative", overflow: "visible" }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMoreIcon />} 
                      className="topics__accordion-summary"
                      sx={{
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          color: "#9A9AA8",
                        },
                        "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                          color: "#6036D6",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" width="100%">
                        <Box display="flex" alignItems="center" gap={1.5}>
                        <Box
                          className={`topics_number_badge ${
                            isDomainExpanded ? "expand" : ""
                          }`}
                        >
                          {dIndex + 1}
                        </Box>

                        <Box>
                          <Typography className={`topics-domain_title ${isDomainExpanded ? "expand" : ""}`}>
                            {domain}
                          </Typography>
                          <Typography className={`topics-domain_count ${isDomainExpanded ? "expand" : ""}`}>
                            {topicCount} {topicCount > 1 ? "topics" : "topic"}
                          </Typography>
                        </Box>
                      </Box>

                      {!isDomainExpanded && Object.keys(classificationMap).length > 0 && (
                        <Box
                          sx={{
                            width: "1px",
                            height: "26px",
                            backgroundColor: "#000000",
                            mx: 1.5,
                          }}
                        />
                      )}

                      {!isDomainExpanded && Object.keys(classificationMap).length > 0 && (
                        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                          {Object.entries(classificationMap).map(([key, { count, labelColor, countColor, icon }]) => {
                            return (
                              <Box
                                key={key}
                                className="topics__badge_domain"
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                {icon && (
                                  <Box
                                    component="img"
                                    src={icon.src}
                                    alt={icon.alt}
                                    sx={{
                                      width: 16,
                                      height: 16,
                                      filter: icon.filter,
                                    }}
                                  />
                                )}
                                <Box component="span" sx={{ color: labelColor }}>
                                  {formatLabel(key)}
                                </Box>{" "}
                                <Box component="span" sx={{ color: countColor }}>
                                  [{count}]
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                    </AccordionSummary>

                    <AccordionDetails
                      className="topics_group_box"
                      sx={{
                        pt: expandedIndex === `d-${dIndex}-0` ? "12px" : undefined,
                      }}
                    >
                      {topics.map((item, i) =>
                        renderTopicAccordion(item, i, `d-${dIndex}-${i}`)
                      )}
                    </AccordionDetails>
                  </Accordion>
                )
              })}

              {standaloneTopics.map((item, index) =>
                renderTopicAccordion(item, index, `standalone-${index}`)
              )}

            </AccordionDetails>
          ) : (
            summaryByTopic.map((item, index) =>
              renderTopicAccordion(item, index, `flat-${index}`)
            )
          )}

        </Box>
      </div>
      <FeedbackPopup
        open={feedbackOpen}
        interactionId={interactionId}
        badgeData={badgeData}
        feedbackReference={feedbackReference}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={handleFeedbackSubmit}
        feedbackKey="topic"
      />
      <ProgressSpinner showSpinner={showSpinner} />
    </>
  );
};

export default TopicsPage;
